/**
 * Super Admin — Audit Plateforme.
 * Combines a live codebase scan (8 criteria) with editable Super Admin notes.
 * Recommended for: technical committees, investment due diligence, governance reviews.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import {
    Loader2, RefreshCw, ShieldCheck, Code2, Copy as CopyIcon, Lock, Zap,
    Layers, AlertTriangle, BookOpen, Workflow, Save, Pencil, X,
    CheckCircle2, MinusCircle, Activity, FileDown, ChevronDown, Handshake,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Visual config ----------
const CRITERION_META = {
    code:           { icon: Code2,           color: '#3b82f6', tone: 'bg-blue-50 text-blue-700' },
    duplication:    { icon: CopyIcon,        color: '#a855f7', tone: 'bg-violet-50 text-violet-700' },
    security:       { icon: Lock,            color: '#ef4444', tone: 'bg-rose-50 text-rose-700' },
    performance:    { icon: Zap,             color: '#f59e0b', tone: 'bg-amber-50 text-amber-700' },
    coherence:      { icon: Layers,          color: '#10b981', tone: 'bg-emerald-50 text-emerald-700' },
    tech_debt:      { icon: AlertTriangle,   color: '#d97706', tone: 'bg-amber-50 text-amber-700' },
    documentation:  { icon: BookOpen,        color: '#6366f1', tone: 'bg-indigo-50 text-indigo-700' },
    maintainability:{ icon: Workflow,        color: '#0d9488', tone: 'bg-teal-50 text-teal-700' },
};

const gradeTone = (g) =>
    g >= 85 ? { label: 'Excellent', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', stroke: '#10b981' } :
    g >= 70 ? { label: 'Bon',       cls: 'bg-blue-50 text-blue-700 border-blue-200',         stroke: '#3b82f6' } :
    g >= 55 ? { label: 'À surveiller', cls: 'bg-amber-50 text-amber-700 border-amber-200',   stroke: '#f59e0b' } :
              { label: 'Critique', cls: 'bg-rose-50 text-rose-700 border-rose-200',         stroke: '#ef4444' };

// ---------- Circular gauge ----------
function CircularGauge({ value, size = 84, stroke = 8, color = '#3b82f6' }) {
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (Math.min(Math.max(value, 0), 100) / 100) * circumference;
    const cx = size / 2;
    const cy = size / 2;
    return (
        <svg width={size} height={size}>
            <circle cx={cx} cy={cy} r={radius} stroke="#f1f1ef" strokeWidth={stroke} fill="none" />
            <circle cx={cx} cy={cy} r={radius} stroke={color} strokeWidth={stroke} fill="none"
                strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
                transform={`rotate(-90 ${cx} ${cy})`}
                style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(.4,0,.2,1)' }} />
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                style={{ fontFamily: 'inherit', fontWeight: 600, fontSize: size * 0.3, fill: '#1f2937' }}>
                {Math.round(value)}
            </text>
        </svg>
    );
}

// ---------- Big Health Index header ----------
function GlobalHealth({ grade, scannedAt, onRefresh, refreshing, onExportPdf, exporting }) {
    const tone = gradeTone(grade);
    const date = scannedAt ? new Date(scannedAt).toLocaleString('fr-FR') : '—';
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;
        const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [menuOpen]);

    const handleClick = (mode) => {
        setMenuOpen(false);
        onExportPdf(mode);
    };

    return (
        <div className="rounded-2xl p-6 lg:p-8 border border-stone-200 bg-gradient-to-br from-primary-light/40 via-white to-stone-50 flex flex-wrap items-center gap-8 justify-between" data-testid="audit-global">
            <div className="flex items-center gap-6">
                <CircularGauge value={grade} size={120} stroke={10} color={tone.stroke} />
                <div>
                    <div className="eyebrow mb-1 flex items-center gap-1.5"><Activity size={11} /> Health Index global</div>
                    <div className="font-heading text-4xl leading-none">{grade}<span className="text-foreground/40 text-2xl">/100</span></div>
                    <div className={`mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium ${tone.cls}`}>
                        {tone.label}
                    </div>
                    <div className="text-[11px] text-foreground/45 mt-2">Dernier scan : {date}</div>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" onClick={onRefresh} disabled={refreshing} data-testid="audit-refresh">
                    <RefreshCw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                    Relancer le scan
                </Button>
                <div className="relative" ref={menuRef}>
                    <Button onClick={() => setMenuOpen(o => !o)} disabled={exporting}
                            className="bg-primary hover:bg-primary-hover" data-testid="audit-export-pdf">
                        {exporting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileDown size={14} className="mr-1.5" />}
                        Exporter en PDF
                        <ChevronDown size={13} className={`ml-1.5 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                    </Button>
                    {menuOpen && (
                        <div className="absolute right-0 mt-2 w-72 rounded-xl border border-stone-200 bg-white shadow-lg z-20 overflow-hidden" data-testid="audit-export-menu">
                            <button onClick={() => handleClick('full')}
                                    className="w-full text-left px-4 py-3 hover:bg-primary-light/40 transition-colors border-b border-stone-100"
                                    data-testid="audit-export-mode-full">
                                <div className="flex items-center gap-2.5">
                                    <Lock size={14} className="text-primary" />
                                    <div className="font-medium text-sm">Rapport complet (interne)</div>
                                </div>
                                <div className="text-[11px] text-foreground/55 mt-0.5 pl-6">
                                    Métriques détaillées + findings techniques + observations
                                </div>
                            </button>
                            <button onClick={() => handleClick('partner')}
                                    className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                                    data-testid="audit-export-mode-partner">
                                <div className="flex items-center gap-2.5">
                                    <Handshake size={14} className="text-emerald-600" />
                                    <div className="font-medium text-sm">Due diligence partenaire</div>
                                </div>
                                <div className="text-[11px] text-foreground/55 mt-0.5 pl-6">
                                    Allégé · sans findings techniques · entête personnalisable
                                </div>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------- Partner export modal ----------
function PartnerExportModal({ open, onClose, onConfirm, loading }) {
    const [recipient, setRecipient] = useState('');
    useEffect(() => { if (open) setRecipient(''); }, [open]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="audit-partner-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                <div className="px-6 py-5 border-b border-stone-100 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <Handshake size={18} />
                    </div>
                    <div>
                        <div className="font-heading text-lg">Due diligence partenaire</div>
                        <div className="text-[11px] text-foreground/55">Rapport allégé · usage restreint</div>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="text-xs font-medium text-foreground/70 mb-1.5 block">
                            Destinataire <span className="text-foreground/40">(facultatif)</span>
                        </label>
                        <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} maxLength={120}
                               placeholder="Ex. Agence Française de Développement, Mme Dupont (BAD), …"
                               className="text-sm" data-testid="audit-partner-recipient-input" autoFocus />
                        <div className="text-[11px] text-foreground/45 mt-1.5">
                            Inscrit en page de garde du rapport. Aide à personnaliser un envoi à un bailleur ou auditeur externe.
                        </div>
                    </div>
                    <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 text-[11px] text-foreground/70 leading-relaxed">
                        <strong className="text-foreground">Contenu de ce rapport :</strong> scores globaux, tableau synthèse 8 dimensions, observations éditoriales du Super Admin. <strong>Exclu :</strong> métriques détaillées, findings techniques, noms de fichiers internes.
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-end gap-2 bg-stone-50">
                    <Button variant="ghost" onClick={onClose} data-testid="audit-partner-cancel">Annuler</Button>
                    <Button onClick={() => onConfirm(recipient)} disabled={loading}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="audit-partner-confirm">
                        {loading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileDown size={14} className="mr-1.5" />}
                        Générer le PDF
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ---------- Single criterion card ----------
function CriterionCard({ c, onSaveNote }) {
    const meta = CRITERION_META[c.key] || { icon: Activity, color: '#6b7280', tone: 'bg-stone-50 text-stone-700' };
    const Icon = meta.icon;
    const grade = c.final_grade != null ? c.final_grade : c.grade;
    const tone = gradeTone(grade);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(c.note || '');
    const [override, setOverride] = useState(c.grade_override != null ? String(c.grade_override) : '');
    const [saving, setSaving] = useState(false);

    useEffect(() => { setDraft(c.note || ''); setOverride(c.grade_override != null ? String(c.grade_override) : ''); }, [c.note, c.grade_override]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const ov = override === '' ? null : Math.max(0, Math.min(100, parseInt(override, 10)));
            await onSaveNote({ criterion_key: c.key, note: draft.trim(), grade_override: Number.isFinite(ov) ? ov : null });
            setEditing(false);
        } finally { setSaving(false); }
    };

    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5 hover:shadow-soft transition-shadow flex flex-col" data-testid={`audit-card-${c.key}`}>
            {/* Header */}
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${meta.tone}`}>
                        <Icon size={18} strokeWidth={1.8} />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-foreground/45">{c.key}</div>
                        <h4 className="font-heading text-base leading-tight">{c.label}</h4>
                    </div>
                </div>
                <CircularGauge value={grade} size={66} stroke={6} color={tone.stroke} />
            </div>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center text-[10px] px-2 py-1 rounded-full border ${tone.cls}`}>
                    {tone.label}
                </span>
                {c.grade_override != null && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-stone-100 text-stone-600 border border-stone-200">
                        Auto : {c.grade}/100 · override
                    </span>
                )}
            </div>

            {/* Metrics */}
            <div className="mt-4 space-y-1.5 text-[12px]">
                {c.metrics.map((m, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-stone-100 pb-1.5 last:border-b-0">
                        <span className="text-foreground/65">{m.label}</span>
                        <span className="font-mono font-medium text-foreground">{m.value}</span>
                    </div>
                ))}
            </div>

            {/* Findings */}
            <div className="mt-4 space-y-1.5">
                {c.findings.slice(0, 6).map((f, i) => (
                    <div key={i} className="text-[12px] text-foreground/70 leading-relaxed pl-1">{f}</div>
                ))}
            </div>

            {/* Note */}
            <div className="mt-auto pt-4 border-t border-stone-100">
                {!editing ? (
                    <div>
                        {c.note ? (
                            <div className="rounded-lg bg-primary-light/30 border border-primary/15 p-3 text-[12px] text-foreground/80 italic leading-relaxed whitespace-pre-line">
                                {c.note}
                            </div>
                        ) : (
                            <div className="text-[11px] text-foreground/45 italic">Aucune note du Super Admin.</div>
                        )}
                        <button onClick={() => setEditing(true)} data-testid={`audit-note-edit-${c.key}`}
                                className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary hover:underline">
                            <Pencil size={11} /> {c.note ? 'Modifier la note' : 'Ajouter une note'}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
                            placeholder="Observation, action prévue, deadline…"
                            className="text-[12px]" data-testid={`audit-note-input-${c.key}`} />
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] text-foreground/60 shrink-0">Override note /100 (facultatif) :</span>
                            <Input type="number" min={0} max={100} value={override}
                                   onChange={(e) => setOverride(e.target.value)}
                                   className="h-8 w-20 text-xs" data-testid={`audit-grade-override-${c.key}`} />
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" onClick={handleSave} disabled={saving}
                                    className="bg-primary hover:bg-primary-hover" data-testid={`audit-note-save-${c.key}`}>
                                {saving ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Save size={12} className="mr-1.5" />}
                                Enregistrer
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(c.note || ''); }}>
                                <X size={12} className="mr-1.5" /> Annuler
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------- Main component ----------
export default function AuditAdmin({ auth }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [partnerOpen, setPartnerOpen] = useState(false);

    const load = async () => {
        setRefreshing(true);
        try {
            const r = await axios.get(`${API}/super-admin/audit/scan`, auth);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Scan échoué');
        } finally { setLoading(false); setRefreshing(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const saveNote = async (payload) => {
        try {
            const r = await axios.put(`${API}/super-admin/audit/notes`, payload, auth);
            const storage = r?.data?.storage;
            toast.success(
                storage === 'supabase'
                    ? 'Note enregistrée (Supabase)'
                    : 'Note enregistrée (fichier local)'
            );
            await load();
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message;
            toast.error(msg || 'Sauvegarde échouée');
            throw e;
        }
    };

    const downloadPdf = async ({ mode = 'full', recipient = '' } = {}) => {
        setExporting(true);
        try {
            const params = { mode };
            if (recipient.trim()) params.recipient = recipient.trim();
            const r = await axios.get(`${API}/super-admin/audit/export.pdf`, {
                ...auth,
                responseType: 'blob',
                params,
            });
            const blob = new Blob([r.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
            const prefix = mode === 'partner' ? 'due-diligence-african-valley' : 'audit-african-valley';
            a.href = url;
            a.download = `${prefix}-${stamp}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success(mode === 'partner' ? 'Rapport due diligence généré' : 'Rapport PDF généré');
            return true;
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Génération PDF échouée');
            return false;
        } finally { setExporting(false); }
    };

    const handleExport = (mode) => {
        if (mode === 'partner') {
            setPartnerOpen(true);
            return;
        }
        downloadPdf({ mode: 'full' });
    };

    const handlePartnerConfirm = async (recipient) => {
        const ok = await downloadPdf({ mode: 'partner', recipient });
        if (ok) setPartnerOpen(false);
    };

    const totals = useMemo(() => {
        if (!data) return null;
        const cs = data.criteria || [];
        const buckets = { excellent: 0, good: 0, watch: 0, critical: 0 };
        cs.forEach(c => {
            const g = c.final_grade ?? c.grade;
            if (g >= 85) buckets.excellent += 1;
            else if (g >= 70) buckets.good += 1;
            else if (g >= 55) buckets.watch += 1;
            else buckets.critical += 1;
        });
        return buckets;
    }, [data]);

    if (loading) return <div className="flex items-center justify-center py-20" data-testid="audit-loading"><Loader2 className="animate-spin" /></div>;
    if (!data) return <div className="text-center py-20 text-foreground/60">Aucun rapport disponible.</div>;

    return (
        <div className="space-y-7" data-testid="audit-admin">
            <p className="text-xs text-foreground/55 max-w-3xl">
                Scan automatique du codebase + notes éditables par le Super Admin.
                Recommandé pour audits trimestriels, due diligence ou comités d'investissement.
            </p>

            {/* Global health card */}
            <GlobalHealth grade={data.global_grade} scannedAt={data.scanned_at} onRefresh={load} refreshing={refreshing}
                          onExportPdf={handleExport} exporting={exporting} />

            <PartnerExportModal open={partnerOpen} onClose={() => setPartnerOpen(false)}
                                onConfirm={handlePartnerConfirm} loading={exporting} />

            {/* Buckets summary */}
            {totals && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="audit-buckets">
                    <BucketChip icon={CheckCircle2} label="Excellent" value={totals.excellent} accent="emerald" />
                    <BucketChip icon={CheckCircle2} label="Bon" value={totals.good} accent="blue" />
                    <BucketChip icon={MinusCircle} label="À surveiller" value={totals.watch} accent="amber" />
                    <BucketChip icon={AlertTriangle} label="Critique" value={totals.critical} accent="rose" />
                </div>
            )}

            {/* Criteria grid */}
            <div className="grid lg:grid-cols-2 gap-5" data-testid="audit-criteria-grid">
                {data.criteria.map((c) => (
                    <CriterionCard key={c.key} c={c} onSaveNote={saveNote} />
                ))}
            </div>

            <div className="text-[11px] text-foreground/45 text-center pt-4">
                Méthodologie : scan read-only des dossiers <code>/app/backend</code>, <code>/app/frontend/src</code>,{' '}
                <code>/app/supabase</code> et <code>/app/memory</code>. Les notes éditables sont stockées dans{' '}
                <code>audit_notes</code> (Supabase) avec RLS Super Admin uniquement.
            </div>
        </div>
    );
}

function BucketChip({ icon: Icon, label, value, accent }) {
    const accents = {
        emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        blue: 'bg-blue-50 text-blue-700 border-blue-200',
        amber: 'bg-amber-50 text-amber-700 border-amber-200',
        rose: 'bg-rose-50 text-rose-700 border-rose-200',
    };
    return (
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${accents[accent]}`}>
            <Icon size={18} />
            <div>
                <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
                <div className="font-heading text-2xl leading-none">{value}</div>
            </div>
        </div>
    );
}
